import React, { useState, useEffect, useRef, useCallback } from "react";

type PromptResolver = (value: string | null) => void;

let globalSetPromptState: ((state: PromptState | null) => void) | null = null;
let currentResolver: PromptResolver | null = null;

interface PromptState {
  message: string;
  placeholder?: string;
}

/**
 * Shows a prompt modal and returns the user's input.
 * Returns null if the user cancels.
 *
 * Usage: const reason = await ShowPrompt("Leave a reason why you are deleting this comment");
 */
export const ShowPrompt = (
  message: string,
  placeholder?: string
): Promise<string | null> => {
  return new Promise((resolve) => {
    if (!globalSetPromptState) {
      console.error(
        "PromptModalProvider not mounted. Make sure to include <PromptModalProvider /> in your app."
      );
      resolve(null);
      return;
    }

    currentResolver = resolve;
    globalSetPromptState({ message, placeholder });
  });
};

/**
 * Provider component that enables the ShowPrompt function.
 * Include this once at the top level of your app.
 */
export const PromptModalProvider = () => {
  const [promptState, setPromptState] = useState<PromptState | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isClosing, setIsClosing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    globalSetPromptState = setPromptState;
    return () => {
      globalSetPromptState = null;
    };
  }, []);

  useEffect(() => {
    if (promptState) {
      setInputValue("");
      setIsClosing(false);
      // Focus the input after a short delay to ensure the modal is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [promptState]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      if (currentResolver) {
        currentResolver(null);
        currentResolver = null;
      }
      setPromptState(null);
    }, 150);
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) return;

    setIsClosing(true);
    setTimeout(() => {
      if (currentResolver) {
        currentResolver(trimmedValue);
        currentResolver = null;
      }
      setPromptState(null);
    }, 150);
  }, [inputValue]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    },
    [handleSubmit, handleClose]
  );

  if (!promptState) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-150 ${
        isClosing ? "opacity-0" : "opacity-100"
      }`}
    >
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div
        className={`relative mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800 transition-transform duration-150 ${
          isClosing ? "scale-95" : "scale-100"
        }`}
      >
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          {promptState.message}
        </h2>
        <div className="mt-4">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={promptState.placeholder || "Enter your response..."}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-400"
            rows={3}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Press Cmd+Enter to submit, Escape to cancel
          </p>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!inputValue.trim()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};
